/* eslint-disable i18next/no-literal-string */
import { dayjs } from 'date';
import { forwardRef, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, FormHelperText, TimePicker, type TimePickerProps } from 'ui';

type Props = TimePickerProps & {
  required?: boolean;
  name: string;
  label: string;
  autocomplete?: string;
  helperText?: string;
  clearable?: boolean;
  size?: 'small' | 'medium';
};

export const TimeField = forwardRef<HTMLDivElement, Props>(function TimeField(
  {
    label,
    name,
    autocomplete,
    disabled = false,
    required = false,
    helperText,
    clearable = true,
    size = 'medium',
    format = 'HH:mm',
    ...rest
  },
  ref
) {
  const { control } = useFormContext();
  const anchorRef = useRef<HTMLDivElement>(null);
  const { slotProps: pickerSlotProps, ...pickerProps } = rest;

  return (
    <Controller
      control={control}
      name={name}
      rules={
        required
          ? {
              required: 'This field is required',
            }
          : {}
      }
      render={({ field, formState }) => {
        const error = formState.errors[name];
        const { value, ...restField } = field;
        return (
          <FormControl required={required} error={Boolean(error)} fullWidth ref={anchorRef}>
            <TimePicker
              label={label}
              disabled={disabled}
              {...restField}
              {...pickerProps}
              format={format}
              ref={ref}
              value={value ? dayjs(value) : null}
              name={autocomplete || name}
              slotProps={{
                ...pickerSlotProps,
                popper: {
                  ...pickerSlotProps?.popper,
                  anchorEl: anchorRef.current,
                },
                textField: {
                  ...pickerSlotProps?.textField,
                  error: Boolean(error),
                  required,
                  size,
                },
                ...(clearable && {
                  clearButton: {
                    ...pickerSlotProps?.clearButton,
                    // @ts-expect-error - data-testid is a valid prop, despite not being in the type
                    'data-testid': 'clear-time-button',
                  },
                  field: {
                    ...pickerSlotProps?.field,
                    clearable: true,
                    onClear: () => field.onChange(null),
                  },
                }),
              }}
            />
            {error || helperText ? (
              <FormHelperText>{error ? String(error?.message) : helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    />
  );
});
